/**
 * AIWatchlistPanel Component (Phase 5 - Task 5.4)
 *
 * AI 추천 종목 패널 (Gemini 2.5 Flash)
 */

import React from 'react';
import type { AIStockData } from '../types/watchlist';
import StockCard from './StockCard';

interface AIWatchlistPanelProps {
  stocks: AIStockData[];
  loading: boolean;
  error: string | null;
  countdown: number;
  onRefresh: () => void;
  onExecute: (ticker: string) => void;
  executingSymbol: string | null;
}

const AIWatchlistPanel: React.FC<AIWatchlistPanelProps> = ({
  stocks,
  loading,
  error,
  countdown,
  onRefresh,
  onExecute,
  executingSymbol,
}) => {
  // 카운트다운 포맷 (시:분:초)
  const formatCountdown = (seconds: number): string => {
    // 카운트다운이 0이면 곧 갱신될 예정 메시지 표시
    if (seconds === 0) {
      return '곧 갱신 예정...';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Gemini API Rate Limit 감지
  const isRateLimitError = error?.includes('429') ||
                          error?.includes('quota') ||
                          error?.includes('Rate Limit') ||
                          error?.includes('할당량');

  return (
    <div className="ai-watchlist-panel">
      <div className="panel-header">
        <h3>AI 단기 분석 후보 (Gemini)</h3>
        <div className="panel-actions">
          <div className="countdown">
            다음 자동 새로고침: {formatCountdown(countdown)}
          </div>
          <button onClick={onRefresh} disabled={loading || isRateLimitError}>
            {loading ? '새로고침 중...' : isRateLimitError ? '일시중지' : '수동 새로고침'}
          </button>
        </div>
      </div>

      {/* Gemini API Rate Limit 경고 */}
      {isRateLimitError && stocks.length > 0 && (
        <div className="rate-limit-warning">
          <div className="warning-header">Gemini API 할당량 초과</div>
          <div className="warning-content">
            <p>• 기존 Watchlist는 계속 사용 가능합니다</p>
            <p>• 새로운 종목 추천은 24시간 후 자동 재개됩니다</p>
            <p>• 수동 새로고침은 일시적으로 비활성화되었습니다</p>
            <p>• 현재 표시된 종목의 전략 신호는 계속 업데이트됩니다</p>
          </div>
        </div>
      )}

      {stocks.length > 0 && (
        <div className="rate-limit-warning">
          <div className="warning-header">후보 해석 기준</div>
          <div className="warning-content">
            <p>• 이 목록은 자동 매수 목록이 아니라 전략 신호를 조회할 단기 분석 후보입니다.</p>
            <p>• 실제 주문은 각 종목 전략 신호와 리스크 게이트가 BUY일 때만 실행됩니다.</p>
          </div>
        </div>
      )}

      {loading && stocks.length === 0 ? (
        <div className="loading">AI 추천 종목 로딩 중...</div>
      ) : error && stocks.length === 0 ? (
        <div className="error">AI Watchlist 오류: {error}</div>
      ) : stocks.length === 0 ? (
        <div className="empty">AI 추천 종목이 없습니다</div>
      ) : (
        <div className="ai-stocks-grid">
          {stocks.map((stock) => (
            <StockCard
              key={stock.ticker}
              stock={stock}
              onExecute={onExecute}
              executing={executingSymbol === stock.ticker}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AIWatchlistPanel;
